import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckIcon,
  PlusIcon,
  Popover,
  PopoverButton,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@playerbase/ui';

import { useShell } from '../use-shell.js';

// Game picker. Lives in the topbar (was previously in the sidebar);
// the surrounding layout owns spacing, so this component renders no
// outer padding wrapper and lets the parent place it directly.

export function GameSwitcher(): JSX.Element {
  const { activeStudio, activeGame, games } = useShell();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  function selectGame(gameShortId: string): void {
    setOpen(false);
    navigate(`/s/${activeStudio.shortId}/g/${gameShortId}/overview`);
  }

  function goToNewGame(): void {
    setOpen(false);
    navigate(`/s/${activeStudio.shortId}/games/new`);
  }

  const buttonLabel = activeGame !== null ? activeGame.name : 'No game yet';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PopoverButton
          size="sm"
          leading={
            <span
              aria-hidden
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-full',
                activeGame !== null ? 'bg-accent' : 'bg-text-dim',
              )}
            />
          }
          className="max-w-[12rem]"
        >
          <span className="flex-1 truncate">{buttonLabel}</span>
        </PopoverButton>
      </PopoverTrigger>
      <PopoverContent className="w-[14rem]" align="start" side="bottom">
        <div className="px-3 pt-2 pb-1 text-[10px] font-mono uppercase tracking-wider text-text-muted">
          Games
        </div>
        {games.length === 0 ? (
          <div className="px-3 pb-2 text-xs text-text-muted">
            No games configured yet.
          </div>
        ) : (
          games.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => selectGame(g.shortId)}
              className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-2"
            >
              <span className="flex-1 truncate">{g.name}</span>
              {activeGame?.id === g.id && <CheckIcon className="text-accent" />}
            </button>
          ))
        )}
        <div className="my-1 border-t border-border" />
        <button
          type="button"
          onClick={goToNewGame}
          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-xs text-text-muted hover:bg-surface-2 hover:text-text"
        >
          <PlusIcon />
          <span>Add a game</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
